import { type PropsWithChildren, useContext } from "react";
import { RESOURCE_NAME } from "common/authorization";
import { variablesOrSkip } from "common-client/utils/apollo";
import { TargetObject } from "common-client/utils/customObject";
import { ObjectType, useGetTasksQuery } from "../../generated/graphql";
import { normalizeQueryResult } from "../../utils/apollo";
import { Section, Wrapper } from "../AddressPanel/__styles__/AddressPanel";
import { AuthContext } from "../Authorization/AuthContext";
import { BANNER_TYPES } from "../Common/__styles__/Banner";
import Banner from "../Common/Banner";
import { Button } from "../Common/Buttons/Button";
import { EmptyState } from "../Common/EmptyState";
import Icon, { Icons } from "../Common/Icons";
import { Body } from "../Common/Typography";
import {
  HeaderContainer,
  ListContainer,
  LoadingContainer,
} from "./__styles__/List";
import { useCreateTaskModal } from "./Form/CreateTaskForm";
import { TaskItem } from "./TaskItem";

function TasksContainer({
  children,
  targetObject,
}: PropsWithChildren<{ targetObject?: Maybe<TargetObject> }>) {
  const { authorized } = useContext(AuthContext);

  const [showTaskModal] = useCreateTaskModal({ targetObject });

  const canCreateTask = authorized({
    resource: RESOURCE_NAME.TASK,
    permission: "create",
  });

  return (
    <Wrapper>
      <Section>
        <HeaderContainer>
          <Body size="large" type="emphasis">
            Tasks
          </Body>
          <Button
            onPress={() => showTaskModal()}
            size="small"
            isDisabled={!canCreateTask}
            data-testid="createTask"
            iconStart="plus"
            variant="primary"
            title="Add new"
          />
        </HeaderContainer>
        {children}
      </Section>
    </Wrapper>
  );
}

export function Tasks({
  targetObject,
  objectType,
}: {
  targetObject?: Maybe<TargetObject>;
  objectType: ObjectType;
}) {
  const { status, data } = normalizeQueryResult(
    useGetTasksQuery({
      ...variablesOrSkip({
        objectId: targetObject?.id,
        objectType,
      }),
      fetchPolicy: "cache-and-network",
    }),
  );

  if (status === "success") {
    if (data.tasks.length > 0) {
      return (
        <TasksContainer targetObject={targetObject}>
          <ListContainer>
            {data.tasks.map(task => (
              <TaskItem key={task.id} task={task} targetObject={targetObject} />
            ))}
          </ListContainer>
        </TasksContainer>
      );
    }

    return (
      <TasksContainer targetObject={targetObject}>
        <EmptyState
          icon="add-file"
          title="No tasks created"
          body="Add a task to get started."
        />
      </TasksContainer>
    );
  }

  if (status === "error") {
    return (
      <TasksContainer targetObject={targetObject}>
        <Banner
          type={BANNER_TYPES.ATTENTION}
          items={["Error loading tasks. Reload and try again."]}
        />
      </TasksContainer>
    );
  }

  if (status === "pending") {
    return (
      <TasksContainer targetObject={targetObject}>
        <LoadingContainer>
          <Icon icon={Icons.LOADING} />
        </LoadingContainer>
      </TasksContainer>
    );
  }

  return status satisfies never;
}
